import React, { FC, useEffect, useRef } from 'react';
import { useAction, useAtom } from '@reatom/npm-react';
import { Icon, Loader } from '@whitesharx/starfall-frontend-ui-kit';
import { t } from '@whitesharx/starfall-frontend-ttranslate';
import { gameApiAtom, movesHistoryAtom, rewindAction, selectMoveAction, selectedMoveAtom } from '../../model';
import {
  buttonStyles,
  buttonsContainerStyles,
  containerStyles,
  endButtonStyles,
  listContainerStyles,
  listItemStyles,
  loaderStyles,
  numberStyles,
  prevButtonStyles,
  startButtonStyles,
  startingPositionStyles,
} from './styles.css';
import classNames from 'classnames';
import GameControlButton from '../GameControlButton';
import { SAN } from '../SAN';
import { toMovesPairs } from '../../utils/game';

type Props = {
  className?: string;
};

const MovesHistoryList: FC<Props> = ({ className }) => {
  const [gameAPI] = useAtom(gameApiAtom);
  const [moves] = useAtom(movesHistoryAtom);
  const [selectedMove] = useAtom(selectedMoveAtom);
  const selectMove = useAction(selectMoveAction);
  const rewind = useAction(rewindAction);
  const pairs = toMovesPairs(moves);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!gameAPI?.gameStarted || !moves.length || !selectedMove) {
      return;
    }

    const lastMove = moves.findIndex((m) => m.fen === selectedMove?.fen) === moves.length - 1;
    if (lastMove) {
      ref.current?.scrollTo({ top: ref.current?.scrollHeight, behavior: 'smooth' });
    }
  }, [selectedMove, gameAPI?.gameStarted, moves]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        rewind('prev');
      }

      if (event.key === 'ArrowUp') {
        rewind('start');
      }

      if (event.key === 'ArrowRight') {
        rewind('next');
      }

      if (event.key === 'ArrowDown') {
        rewind('end');
      }
    };

    window.addEventListener('keydown', handler);

    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, []);

  const renderContent = () => {
    if (!gameAPI?.gameStarted) {
      return (
        <div>
          <Loader.Mini className={loaderStyles} />
        </div>
      );
    }

    if (!pairs.length) {
      return <p className={startingPositionStyles}>{t('game.starting-position')}</p>;
    }

    return (
      <ul className={listContainerStyles}>
        {pairs.map((pair) => (
          <li key={pair.number} className={listItemStyles}>
            <span className={numberStyles}>{pair.number}.</span>

            <span>
              <SAN
                value={pair.white.san}
                color="w"
                onClick={() => selectMove(pair.white, true)}
                selected={selectedMove?.fen === pair.white.fen}
              />
            </span>

            {pair.black && (
              <span>
                <SAN
                  value={pair.black.san}
                  color="b"
                  onClick={() => selectMove(pair.black!, true)}
                  selected={selectedMove?.fen === pair.black.fen}
                />
              </span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={className}>
      <div className={classNames(containerStyles, 'scrollStyle')} ref={ref}>
        {renderContent()}
      </div>

      <div className={buttonsContainerStyles}>
        <GameControlButton
          icon={<Icon name="chevronPrevious" />}
          className={classNames(buttonStyles, startButtonStyles)}
          onClick={() => rewind('start')}
        />
        <GameControlButton
          icon={<Icon name="leftArrow" />}
          className={classNames(buttonStyles, prevButtonStyles)}
          onClick={() => rewind('prev')}
        />
        <GameControlButton icon={<Icon name="rightArrow" />} className={buttonStyles} onClick={() => rewind('next')} />
        <GameControlButton
          icon={<Icon name="chevronNext" />}
          className={classNames(buttonStyles, endButtonStyles)}
          onClick={() => rewind('end')}
        />
      </div>
    </div>
  );
};

export default MovesHistoryList;
